import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

interface TooltipProps {
    content: string;
    visible: boolean;
    x: number;
    y: number;
    ric?: string;
}

// Sentinel a metric value can hold while its data is still loading; rendered as
// a small spinner instead of text.
export const TOOLTIP_LOADING_MARKER = '__loading__';

const renderMetricValue = (value: string): React.ReactNode => {
    if (value.trim() === TOOLTIP_LOADING_MARKER) {
        return <span className="tooltip-loading-spinner" />;
    }
    return value;
};

const Tooltip: React.FC<TooltipProps> = ({ content, visible, x, y }) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    if (!mounted || !visible || !content) return null;

    // Function to format market cap with appropriate units
    const formatToKMB = (value: number): string => {
        let units = ['', 'K', 'M', 'B', 'T'];
        let unitIdx = 0;
        let val = Math.abs(value)*1000000;
        
        while (val > 999 && unitIdx < units.length - 1) {
            unitIdx++;
            val = val / 1000;
        }

        // Format to 3 decimal places and add unit
        return val.toFixed(3) + units[unitIdx];
    }

    // Up/down colour for a rendered metric. "-" (no data from the feed) and 0
    // stay neutral instead of being painted as a fall.
    const signClass = (raw: string): string => {
        const num = Number(raw.replace(/[+%,\s]/g, ''));
        if (!isFinite(num) || num === 0) return '';
        return num > 0 ? 'positive' : 'negative';
    }

    const tooltipContent = (
        <div 
            className="stock-tooltip body-tooltip"
            style={{
                position: 'absolute',
                left: x,
                top: y,
                zIndex: 100001,
                pointerEvents: 'none'
            }}
        >
            {content.split('\n').map((line, index) => {
                if (index === 0) {
                    // Stock ticker - bold and larger
                    return <div key={index} className="tooltip-ticker">{line}</div>;
                } else if (index === 1) {
                    // Company name
                    return <div key={index} className="tooltip-company">{line}</div>;
                } else if (index === 2) {
                    // Industry
                    return <div key={index} className="tooltip-industry">{line}</div>;
                } else if (index === 3) {
                    // Empty line separator
                    return <div key={index} className="tooltip-separator"></div>;
                } else if (line.startsWith('Last:')) {
                    // Last price
                    return <div key={index} className="tooltip-metric"><div className="tooltip-metric-label">Last </div> <div className="tooltip-metric-label-right">{renderMetricValue(line.replace('Last:', ''))}</div></div>;
                } else if (line.startsWith('NetChg:')) {
                    // Net change - green for positive, red for negative
                    return <div key={index} className={`tooltip-metric ${signClass(line.replace('NetChg:', ''))}`}><div className="tooltip-metric-label">NetChg </div> <div className="tooltip-metric-label-right">{line.replace('NetChg:', '')}</div></div>;
                } else if (line.startsWith('PerChg:')) {
                    // Percentage change - green for positive, red for negative
                    return <div key={index} className={`tooltip-metric ${signClass(line.replace('PerChg:', ''))}`}><div className="tooltip-metric-label">PerChg </div> <div className="tooltip-metric-label-right">{line.replace('PerChg:', '')}</div></div>;
                } else if (line.startsWith('Turnover:')) {
                    // Turnover
                    return <div key={index} className="tooltip-metric"><div className="tooltip-metric-label">Turnover </div> <div className="tooltip-metric-label-right">{line.replace('Turnover:', '')}</div></div>;
                } else if (line.startsWith('Vol:')) {
                    // Volume
                    return <div key={index} className="tooltip-metric"><div className="tooltip-metric-label">Vol </div> <div className="tooltip-metric-label-right">{line.replace('Vol:', '')}</div></div>;
                } else if (line.startsWith('Mkt Cap:')) {
                    // Market Cap. The value is "-" when the feed has none, so
                    // pass it through instead of running it back through Number().
                    const mktCap = line.replace('Mkt Cap:', '').trim();
                    return <div key={index} className="tooltip-metric"><div className="tooltip-metric-label">Mkt Cap </div> <div className="tooltip-metric-label-right">{isFinite(Number(mktCap)) && mktCap !== '' ? formatToKMB(Number(mktCap)) : mktCap}</div></div>;
                } else {
                    const parts = line.split(':');
                    if (parts.length >= 2) {
                        if(parts[0].startsWith('Perf')){
                            return <div key={index} className={`tooltip-metric ${signClass(line.replace(parts[0]+':', ''))}`}><div className="tooltip-metric-label">{parts[0] } </div> <div className="tooltip-metric-label-right">{line.replace(parts[0]+':', '')}</div></div>;
                        }
                        return <div key={index} className="tooltip-metric"><div className="tooltip-metric-label">{parts[0]} </div> <div className="tooltip-metric-label-right">{renderMetricValue(parts.slice(1).join(':'))}</div></div>;
                    } else {
                        return <div key={index} className="tooltip-metric">{line}</div>;
                    }
                }
            })}
        </div>
    );

    return createPortal(tooltipContent, document.body);
};

export default Tooltip;
